import { AUTHORITIES } from "@/config/constants";
import { useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoSetting } from "@/shared/model/edo-setting.model";
import { EdoSettingEnum } from "@/shared/model/enumerations/edo-setting.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui";
import axios from "axios";
import { SocketContext } from "context/socket";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const StudentPopUpTimeout = () => {
  const TIMEOUT = 1000 * 10;
  const navigate = useNavigate();
  const socket = useContext(SocketContext);
  const [open, setOpen] = useState<boolean>(false);
  const [timeoutValue, setTimeoutValue] = useState<number | null>(null);
  const [idleTime, setIdleTime] = useState<number>(0);
  const [countdown, setCountdown] = useState<number>(TIMEOUT / 1000);
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );

  const interval = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleGetTimeout = async () => {
    const requestUrl = `/settings/${EdoSettingEnum.STUDENT_POPUP_TIMEOUT}`;
    try {
      const { data } = await axios.get<{ data: IEdoSetting }>(requestUrl);
      if (data?.data?.value && parseInt(data.data.value.value, 10) > 0) {
        setTimeoutValue(parseInt(data.data.value.value, 10));
      }
    } catch (e) {
      setTimeoutValue(null);
    }
  };

  const handleKeepAlive = () => {
    setOpen(false);
    if (timeout.current) clearTimeout(timeout.current);
    if (interval.current) clearInterval(interval.current);
    setCountdown(TIMEOUT / 1000);
    setIdleTime(0);
  };

  const handleLogout = () => {
    navigate("/logout");
  };

  const handleStartCountdown = () => {
    setOpen(true);
    interval.current = setInterval(() => {
      setCountdown((prevValue) => prevValue - 1);
    }, 1000);
  };

  useEffect(() => {
    if (
      hasAnyAuthority(account?.roles ?? [], [AUTHORITIES.ROLE_STUDENT]) &&
      timeoutValue !== null &&
      idleTime === timeoutValue * 60 - TIMEOUT / 1000
    ) {
      if (timeout.current) clearTimeout(timeout.current);
      if (interval.current) clearInterval(interval.current);
      handleStartCountdown();
    }
  }, [account?.roles, timeoutValue, idleTime]);

  const handleKeepAliveEvent = () => {
    if (
      timeoutValue !== null &&
      idleTime <= timeoutValue * 60 - TIMEOUT / 1000
    ) {
      setIdleTime(0);
    }
  };

  useEffect(() => {
    if (
      hasAnyAuthority(account?.roles ?? [], [AUTHORITIES.ROLE_STUDENT]) &&
      timeoutValue !== null
    ) {
      window.document.addEventListener("mousemove", handleKeepAliveEvent);
      window.document.addEventListener("keydown", handleKeepAliveEvent);
    }
    return () => {
      window.document.removeEventListener("mousemove", handleKeepAliveEvent);
      window.document.removeEventListener("keydown", handleKeepAliveEvent);
    };
  }, [account?.roles, timeoutValue]);

  useEffect(() => {
    handleGetTimeout();
    socket?.emit("keep-alive");
    const keepAliveInterval = setInterval(() => socket?.emit("keep-alive"), 1000 * 60 * 5);
    const idleInterval = setInterval(() => setIdleTime((prev) => prev + 1), 1000);
    return () => {
      clearInterval(keepAliveInterval);
      clearInterval(idleInterval);
      if (interval.current) clearInterval(interval.current);
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      if (interval.current) clearInterval(interval.current);
      handleLogout();
    }
  }, [countdown]);

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Stai ancora lavorando?</DialogTitle>
        </DialogHeader>
        <p className="text-body text-[var(--color-mockup-text-mid)]">
          Se non hai ancora finito di lavorare, clicca su &quot;Continua&quot; per
          mantenere la sessione attiva. Altrimenti, clicca su &quot;Esci&quot; per
          terminare la sessione.
          <br />
          <br />
          {open && `La sessione scadrà tra ${countdown} secondi.`}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={handleLogout}>
            Esci
          </Button>
          <Button onClick={handleKeepAlive}>Continua</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StudentPopUpTimeout;
